import { createFileRoute, Link } from '@tanstack/react-router'

import { EmptyState } from '~/components/empty-state'
import { FoodMark } from '~/components/food-mark'
import { ChevronLeftIcon, FoodIcon } from '~/components/icons'
import { Skeleton, SkeletonBlock } from '~/components/skeleton'
import { TargetBar } from '~/components/target-bar'
import { todayKey } from '~/data/weighins'
import {
  NUTRIENT_COLORS,
  NUTRIENT_LABELS,
  TARGETED,
  totalOn,
  formatNutrient,
  formatShare,
  goalOn,
  type Targeted,
} from '~/data/nutrition'
import { useDayEntries, useNutrition } from '~/data/nutrition-store'
import { dayStart, formatDayLabel } from '~/lib/dates'
import { dialogDayOf, withoutDialog, type DialogSearch } from '~/lib/dialogs'
import { contributorsOn, nutrientStretches } from '~/lib/nutrient-page'

interface NutrientSearch extends DialogSearch {
  /** The day the page is about. Absent means today, which is never written. */
  readonly day?: string
}

const isTargeted = (name: string): name is Targeted =>
  (TARGETED as readonly string[]).includes(name)

export const Route = createFileRoute('/diary_/nutrients/$nutrient')({
  validateSearch: (search: Record<string, unknown>): NutrientSearch => {
    const day = dialogDayOf(search.day)
    return day === null ? {} : { day }
  },
  component: NutrientPage,
})

/**
 * A nutrient's own page, per `/specs/features/nutrition.spec.md`: what it came to yesterday,
 * over the past week and over the past month, each against the target of the same stretch; the
 * target in force; and what the day it was opened on was made of for it.
 *
 * The stretches are read against a target rather than against the largest of them: a week is
 * seven days of the target and a month thirty, so each bar carries a mark where its own target
 * falls and a reading past it draws past the mark.
 */
function NutrientPage() {
  const { nutrient } = Route.useParams()
  const { day: asked } = Route.useSearch()
  const data = useNutrition()
  const on = asked ?? todayKey()
  const onDay = useDayEntries(on)
  const known = isTargeted(nutrient)
  const name: Targeted = known ? nutrient : 'calories'
  const label = known ? NUTRIENT_LABELS[name] : 'Nutrient'
  const color = NUTRIENT_COLORS[name]

  const head = (
    <div className="flex items-center gap-2">
      <Link
        to="/diary"
        search={(prev) => ({ ...withoutDialog(prev), ...(asked === undefined ? {} : { day: on }) })}
        aria-label="Back to the diary"
        className="icon-btn -ml-2"
      >
        <ChevronLeftIcon aria-hidden className="size-7" />
      </Link>
      <h1 className="flex-1 text-3xl font-semibold">{label}</h1>
    </div>
  )

  /*
   * A nutrient nothing targets has no page of its own: there would be nothing to read it
   * against, and nothing on the day's head or the metrics row leads here for one. An address
   * naming one says so rather than quietly answering about the calories instead.
   */
  if (!known) {
    return (
      <div className="mx-auto max-w-3xl space-y-6">
        {head}
        <EmptyState
          icon={<FoodIcon className="size-8" />}
          title="No such nutrient"
          detail="Only the calories and the macros have a page of their own."
        />
      </div>
    )
  }

  // Nothing is said about a stretch before the days have arrived.
  if (data === null) {
    return (
      <div className="mx-auto max-w-3xl space-y-6">
        {head}
        <SkeletonBlock label={`Loading ${label}`} className="space-y-4">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-16 w-full" />
        </SkeletonBlock>
      </div>
    )
  }

  const stretches = nutrientStretches(data, name, on)
  // Nothing is said of the day's makings until its entries have arrived, either.
  const contributors = onDay === null ? null : contributorsOn(onDay, name)
  const total = totalOn(data, on)?.[name] ?? 0
  const target = goalOn(data.goals, on)?.[name] ?? null

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {head}

      <section
        aria-label="Against the target"
        className="space-y-5 rounded-2xl bg-surface-raised p-4 ring-1 ring-white/10"
      >
        {stretches.map((stretch) => {
          const share =
            stretch.target === null || stretch.target <= 0 ? null : stretch.total / stretch.target
          return (
            <div
              key={stretch.key}
              role="group"
              className="space-y-1.5"
              aria-label={`${stretch.label} ${formatNutrient(name, stretch.total)}${
                share === null ? '' : `, ${formatShare(share)} of the target`
              }`}
            >
              <div className="flex items-baseline gap-2">
                <span className="flex-1 text-sm text-ink-muted">{stretch.label}</span>
                <span className="text-sm">
                  {stretch.target === null
                    ? formatNutrient(name, stretch.total)
                    : `${formatNutrient(name, stretch.total)} / ${formatNutrient(name, stretch.target)}`}
                </span>
                {share !== null && (
                  <span className="text-sm font-medium" style={{ color }}>
                    {formatShare(share)}
                  </span>
                )}
              </div>
              <TargetBar
                label={`${stretch.label} against the target`}
                eaten={stretch.total}
                target={stretch.target}
                color={color}
              />
            </div>
          )
        })}
      </section>

      <section
        aria-label="The target in force"
        className="rounded-2xl bg-surface-raised p-4 ring-1 ring-white/10"
      >
        <p className="text-xs tracking-wider text-ink-muted uppercase">Target a day</p>
        <p className="text-2xl" style={{ color }}>
          {target === null ? 'No goal set' : formatNutrient(name, target)}
        </p>
        <p className="mt-1 text-sm text-ink-muted">
          {formatDayLabel(dayStart(on))} stands at {formatNutrient(name, total)}
        </p>
      </section>

      <section aria-label="What the day was made of" className="space-y-3">
        <h2 className="px-1 text-xs font-medium tracking-wider text-ink-muted uppercase">
          What made up {formatDayLabel(dayStart(on))}
        </h2>
        {contributors === null ? (
          <SkeletonBlock label="Loading what the day was made of" className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </SkeletonBlock>
        ) : contributors.length === 0 ? (
          <EmptyState
            icon={<FoodIcon className="size-8" />}
            title={`Nothing logged holds any ${label.toLowerCase()}`}
            detail="Log something that does and it stands here."
          />
        ) : (
          <ul className="divide-y divide-white/5 rounded-2xl bg-surface-raised ring-1 ring-white/10">
            {contributors.map(({ entry, held }) => {
              const share = total > 0 ? held / total : null
              return (
                <li
                  key={entry.id}
                  className="flex items-center gap-3 px-4 py-3"
                  aria-label={`${entry.name}, ${formatNutrient(name, held)}${
                    share === null ? '' : `, ${formatShare(share)} of the day`
                  }`}
                >
                  {entry.food === null ? (
                    <FoodMark name={entry.name} />
                  ) : (
                    <FoodMark food={entry.food} />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{entry.name}</span>
                    <span className="block truncate text-sm text-ink-muted">
                      {formatNutrient(name, held)} · {entry.time}
                    </span>
                  </span>
                  <span className="text-lg font-medium" style={{ color }}>
                    {share === null ? '—' : formatShare(share)}
                  </span>
                </li>
              )
            })}
          </ul>
        )}
      </section>
    </div>
  )
}
